import * as React from 'react';
import { useAuth } from '@leagueplatform/auth-utils';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useFilterEntitiesBasedOnPermissions,
  useConfigApp,
  useGetPermissions,
  useAuthoringEnvironments,
  useConfigAppParams,
} from '@web-config-app/core-react';
import { ErrorPage } from '@web-config-app/core-react-ui';
import { AuthViewLogout } from '../../pages/auth/Auth.pages';
import { LoadingIndicator } from '../loading-indicator.component';
import { useTenantId } from '../../hooks/use-tenant-id/use-tenant-id';
import { useUpdateTenantConfig } from '../../hooks/use-update-tenant-config/use-update-tenant-config';
import { setAmplitudeUserId } from '../../utils/amplitude';
import { captureErrorWithTenant } from '../../utils/sentry-helpers';

/**
 * Inner component that runs hooks depending on `configApp` (permissions,
 * authoring environments, entity filtering). Only rendered after auth is
 * confirmed, which guarantees LeagueProvider has fully mounted with the real
 * tenant config so `useConfigApp()` returns a valid value.
 */
const AuthorizedPermissionsGuard = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}): React.ReactElement | null => {
  const { isAuthenticated } = useAuth({ redirectToLogInWhenSignedOut: true });
  const { permissionsFilterComplete, tenantId: resolvedTenantId } =
    useConfigApp();
  const { environmentKey, tenantId: routeTenantId } = useConfigAppParams();
  const { currentAuthoringEnvironment } = useAuthoringEnvironments({
    environmentKey,
  });
  const tenantId = routeTenantId ?? resolvedTenantId;
  const { status: permissionsRequestStatus } = useGetPermissions({
    environmentKey: currentAuthoringEnvironment,
    tenantId,
    isAuthenticated,
  });

  const lastCapturedPermissionsErrorKeyRef = React.useRef<string | null>(null);

  useUpdateTenantConfig({
    isAuthenticated,
    tenantId,
  });

  useFilterEntitiesBasedOnPermissions({
    isAuthenticated,
    permissionsRequestStatus,
    environmentKey: currentAuthoringEnvironment,
    tenantId,
  });

  React.useEffect(() => {
    if (!isAuthenticated || permissionsRequestStatus !== 'error') {
      lastCapturedPermissionsErrorKeyRef.current = null;
      return;
    }

    const key = `${tenantId ?? ''}|permissions:error`;
    if (lastCapturedPermissionsErrorKeyRef.current === key) return;
    lastCapturedPermissionsErrorKeyRef.current = key;

    captureErrorWithTenant(
      new Error('Unable to access user permissions'),
      tenantId,
    );
  }, [isAuthenticated, permissionsRequestStatus, tenantId]);

  if (isAuthenticated && permissionsRequestStatus === 'error') {
    return (
      <ErrorPage
        secondaryCta={{
          label: 'LOG_OUT',
          to: '/sign-out',
        }}
        heading="PERMISSIONS_ERROR_HEADING"
      />
    );
  }
  if (permissionsRequestStatus === 'pending' || !permissionsFilterComplete) {
    return <LoadingIndicator />;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

/**
 * Authorized routes wrapper for the multi-tenant login flow.
 *
 * Split into two components so that hooks depending on `configApp`
 * (`useConfigApp`, `useAuthoringEnvironments`, etc.) only run after auth is
 * confirmed. During the LeagueProvider config transition (discovery → tenant config),
 * `configApp` is briefly undefined — calling those hooks before auth is ready
 * would crash with "Cannot read properties of undefined".
 *
 * The outer component handles auth state and access_denied errors.
 * The inner `AuthorizedPermissionsGuard` handles permissions and entity
 * filtering, only rendering once `isAuthenticated` is true.
 */
export const MultiTenantAuthorizedRoutes = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}): React.ReactElement | null => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, error: authError } = useAuth({
    redirectToLogInWhenSignedOut: true,
  });
  const { setTenantId } = useTenantId();

  React.useEffect(() => {
    if (isAuthenticated) {
      setAmplitudeUserId().catch(() => {});
    }
  }, [isAuthenticated]);

  // In multi-tenant flow, access_denied from Auth0 means the user's org
  // doesn't have access. Clear the stored tenant and navigate to root so the
  // discovery flow can restart cleanly (without re-resolving the denied tenant).
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasAccessDeniedInUrl = searchParams.get('error') === 'access_denied';
    const hasAccessDeniedError = authError?.type === 'access_denied';

    if (hasAccessDeniedInUrl || hasAccessDeniedError) {
      setTenantId('');
      navigate('/', { replace: true });
    }
  }, [location.search, authError, navigate, setTenantId]);

  if (authError) {
    return <AuthViewLogout />;
  }
  if (!isAuthenticated) {
    return <LoadingIndicator />;
  }

  return <AuthorizedPermissionsGuard>{children}</AuthorizedPermissionsGuard>;
};
